import { WizardLauncherService } from '../../wizard.launcher.service';
import { TimeoutWrapper, UserService } from '@nrwl/common-lib';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  EventEmitter,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { get, isEmpty, merge, set } from 'lodash';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { IWizardEntry } from '../../wizard.entry';
import { WizardComponent } from '../../wizard.component';
import { ApplicationWizardService } from '../../services/application/application.wizard.service';
import { ApplicationBasicHtmlComponent } from './app.basic.page/application.basic.component';
import { ActiveTestHtmlComponent } from './app.active.test.page/active.test.component';
import {
  APP_TYPE_KEY,
  ApplicationType,
  ReviewHtmlComponent
} from './app.review.page/review.component';
import { CreateResultsHtmlComponent } from './app.final.page/create.results.component';
import { NavigationProvider } from '@nrwl/navigation-abstract';

@Component({
  selector: 'new-application-wizard',
  providers: [ApplicationWizardService],
  templateUrl: '../../wizard.template.html',
  styleUrls: ['../../wizard.component.style.scss', '../../wizard.button.style.scss']
})
export class NewApplicationWizardComponent
  extends WizardComponent
  implements OnInit, AfterViewInit, OnDestroy, IWizardEntry {
  @ViewChild('contentPlaceholder', { read: ViewContainerRef, static: true })
  contentRef: ViewContainerRef;
  @ViewChild('subContentPlaceholder', { read: ViewContainerRef, static: true })
  subContentRef: ViewContainerRef;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  wizardConfig: any;
  wizardId: string;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClose = new EventEmitter<any>();
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  dataId: any;
  router: Router;
  wizardService: ApplicationWizardService;
  userService: UserService;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  openWizard: any;
  subscription: Subscription;
  changeDetectorRef: ChangeDetectorRef;
  activatedRouteSubscription: Subscription;

  private readonly timeout = new TimeoutWrapper();

  constructor(
    router: Router,
    wizardService: ApplicationWizardService,
    userService: UserService,
    changeDetectorRef: ChangeDetectorRef,
    private readonly activatedRoute: ActivatedRoute,
    private readonly launcher: WizardLauncherService,
    private readonly factoryResolver: ComponentFactoryResolver,
    private readonly navigator: NavigationProvider
  ) {
    super();
    this.router = router;
    this.wizardService = wizardService;
    this.userService = userService;
    this.changeDetectorRef = changeDetectorRef;
  }

  ngOnInit() {
    this.activatedRouteSubscription = this.activatedRoute.queryParams.subscribe(data => {
      const defaultParams = merge({}, this.dataId, JSON.parse(get(data, 'defaultParams', '{}')));
      this.wizardService.editMode = this.wizardId === 'update_application';
      if (this.wizardService.editMode) {
        // Assigining Application type intially which used to get application type in step components
        const appType = !isEmpty(defaultParams.url)
          ? ApplicationType.CLOUD_BASED
          : ApplicationType.ON_PREMISE;
        set(defaultParams, APP_TYPE_KEY, appType);
        defaultParams.curlUrl = defaultParams.curlUrl ? atob(defaultParams.curlUrl) : null;
        this.updateAppDependencies(defaultParams);
      }
      this.wizardService.openWizard(this.wizardId, this.wizardConfig, defaultParams);
    });
    this.wizardService.registerToggleListener(this.notifyOnToggleSubContent.bind(this));
  }

  ngAfterViewInit() {
    this.createContentComponent();
    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.activatedRouteSubscription) {
      this.activatedRouteSubscription.unsubscribe();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  updateAppDependencies(defaultParams: any): void {
    const dependencies = [];
    if (defaultParams.appDependencies && defaultParams.appDependencies.length > 0) {
      defaultParams.appDependencies.forEach(a => dependencies.push({ id: a.id }));
    }
    defaultParams.appDependencies = dependencies;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  get(object: any, path: string): any {
    return get(object, path);
  }

  //-------------- Control && Validation -----------------------
  toggleNext(): void {
    this.wizardService.nextStep();
    this.createContentComponent();
  }

  toggleBack(): void {
    this.wizardService.previousStep();
    this.createContentComponent();
  }

  toggleFinish(): void {
    const result = this.wizardService.onFinish();
    result.subscribe(() => {
      this.createContentComponent();
    });
  }

  retryOperation(): void {
    // right now there is just one operation that might have failed - in our case, this is creation of app
    // so I'll just call the original logic that takes care to retry 'create'.
    // in the long run/when full wizard is done/by adding active tests/the method will try to figure out
    // what has failed, and rerun just that op. (or those ops)

    this.wizardService.retrySaveRequest();
  }

  editBasicInfo(): void {
    this.wizardService.goToStep('app_basic');
    this.createContentComponent();
  }

  toggleClose(): void {
    this.wizardService.toggleClose(() => this.onClose.emit(true));
  }

  toggleReOpen(): void {
    this.wizardService.reOpen();
  }

  notifyOnToggleSubContent(onOff: boolean): void {
    if (onOff) {
      this.createSubContentComponent();
    } else {
      this.subContentRef.clear();
    }
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  getWizardService(): any {
    return this.wizardService;
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  getFactoryResolver(): any {
    return this.factoryResolver;
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  getContentViewRef(): any {
    return this.contentRef;
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  getSubContentViewRef(): any {
    return this.subContentRef;
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  resolveComponentType(pageId: string): any {
    switch (pageId) {
      case 'application.basic.html':
        return ApplicationBasicHtmlComponent;
      case 'active.test.html':
        return ActiveTestHtmlComponent;
      case 'review.html':
        return ReviewHtmlComponent;
      case 'create.results.html':
        return CreateResultsHtmlComponent;
      default: {
        console.error('Unrecognized template name:', pageId);
        return null;
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  skipStep(onClick: Function): Subscription {
    return this.activatedRoute.queryParams.subscribe(queryParams => {
      const defaultParams = JSON.parse(get(queryParams, 'defaultParams', '{}'));
      const selectedValue = get(defaultParams, 'application-type');
      if (selectedValue) {
        //Need to give time for detectChanges
        this.timeout.add(() => {
          this.toggleNext();
          this.wizardService.dirtyFlag = false;
        }, 0);
        onClick(selectedValue);
      }
    });
  }
}
